import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { clients as clientsApi } from '@/api/endpoints'
import { Checkbox, Field, Input, Select, Spinner, Textarea } from '@/components/ui'
import { useBootstrap } from '@/hooks/useBootstrap'
import type { Client } from '@/types/api'

/** Polia, ktoré formulár posiela. Zvyšok je dopočítaný alebo systémový.
 *  `is_beginner` tu nie je - server ho odvodí z `levelid`. */
type Editable = Pick<
  Client,
  'fname' | 'sname' | 'email' | 'tel' | 'note' | 'levelid' | 'lang_id' | 'course_id'
> & { is_children: boolean }

function toForm(client: Client): Editable {
  return {
    fname: client.fname ?? '',
    sname: client.sname ?? '',
    email: client.email ?? '',
    tel: client.tel ?? '',
    note: client.note ?? '',
    levelid: client.levelid,
    lang_id: client.lang_id,
    course_id: client.course_id,
    is_children: client.is_children,
  }
}

/** Prázdny formulár - nový klient nemá prednastavené nič okrem toho, čo
 *  server aj tak doplní. Predvyplnená úroveň či jazyk by boli tvrdenie
 *  o človeku, ktorého ešte nikto nevidel. */
const EMPTY: Editable = {
  fname: '',
  sname: '',
  email: '',
  tel: '',
  note: '',
  levelid: null,
  lang_id: null,
  course_id: null,
  is_children: false,
}

/**
 * Údaje klienta - ten istý formulár pre úpravu aj pre nového.
 *
 * Bez `client` zakladá, s ním upravuje. Dva takmer rovnaké formuláre by sa
 * rozišli pri prvom pridanom poli; toto je jedno miesto, kde je pravda o tom,
 * čo sa o klientovi eviduje.
 *
 * Pri úprave sa posiela celý formulár, nie len zmenené polia: server je
 * `PATCH`, takže vynechané pole by ostalo po starom - a to je pri mazaní
 * hodnoty (napríklad vymazanie e-mailu) presne to, čo používateľ nechce.
 */
export default function ClientForm({
  client,
  onDone,
}: {
  client?: Client
  onDone: (client: Client) => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { data: codebooks } = useBootstrap()
  // Ponúkajú sa len jazyky, ktoré škola učí; vypnuté zostávajú v číselníku
  // kvôli starším záznamom.
  const active = (codebooks?.languages ?? []).filter((row) => row.use !== false)

  const [form, setForm] = useState<Editable>(() => (client ? toForm(client) : EMPTY))
  const set = <K extends keyof Editable>(key: K, value: Editable[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  const save = useMutation({
    mutationFn: () => (client ? clientsApi.update(client.id, form) : clientsApi.create(form)),
    onSuccess: async (saved) => {
      qc.setQueryData(['client', saved.id], saved)
      await qc.invalidateQueries({ queryKey: ['clients'] })
      // Zoznam duplicít stojí na telefóne a e-maile, takže sa mohol práve zmeniť.
      void qc.invalidateQueries({ queryKey: ['client-duplicates'] })
      onDone(saved)
    },
  })

  const fieldError = (name: keyof Editable) => {
    const data = (save.error as { response?: { data?: Record<string, unknown> } })?.response?.data
    const value = data?.[name]
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0]
    return typeof value === 'string' ? value : undefined
  }

  return (
    <form
      className="space-y-3 rounded-md border border-slate-200 p-3"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('reservation.firstname')} error={fieldError('fname')}>
          <Input value={form.fname ?? ''} onChange={(e) => set('fname', e.target.value)} />
        </Field>
        <Field label={t('reservation.surname')} error={fieldError('sname')}>
          <Input value={form.sname ?? ''} onChange={(e) => set('sname', e.target.value)} />
        </Field>
        <Field label={t('reservation.phone')} error={fieldError('tel')}>
          <Input value={form.tel ?? ''} onChange={(e) => set('tel', e.target.value)} />
        </Field>
        <Field label={t('reservation.email')} error={fieldError('email')}>
          <Input
            type="email"
            value={form.email ?? ''}
            onChange={(e) => set('email', e.target.value)}
          />
        </Field>

        <Field label={t('clients.level')} error={fieldError('levelid')}>
          <Select
            value={form.levelid ?? ''}
            onChange={(e) => set('levelid', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">—</option>
            {(codebooks?.levels ?? []).map((level) => (
              <option key={level.id} value={level.id}>
                {level.name || level.key}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('reservation.language')} error={fieldError('lang_id')}>
          <Select
            value={form.lang_id ?? ''}
            onChange={(e) => set('lang_id', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">—</option>
            {active.map((lang) => (
              <option key={lang.id} value={lang.id}>
                {lang.name || lang.key}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('reservation.course')} error={fieldError('course_id')}>
          <Select
            value={form.course_id ?? ''}
            onChange={(e) => set('course_id', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">—</option>
            {(codebooks?.courses ?? []).map((course) => (
              <option key={course.id} value={course.id}>
                {course.name || course.key}
              </option>
            ))}
          </Select>
        </Field>

        {/* „Začiatočník“ tu nie je - je to tá istá informácia ako úroveň
            a odvodí sa z nej pri uložení. */}
        <div className="flex items-end pb-1">
          <Checkbox
            label={t('global.child')}
            checked={form.is_children}
            onChange={(e) => set('is_children', e.target.checked)}
          />
        </div>
      </div>

      <Field label={t('global.note')} error={fieldError('note')}>
        <Textarea value={form.note ?? ''} onChange={(e) => set('note', e.target.value)} />
      </Field>

      {save.isError && !Object.keys(form).some((key) => fieldError(key as keyof Editable)) ? (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {t('clients.saveFailed')}
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <button type="submit" className="btn-primary" disabled={save.isPending}>
          {save.isPending ? <Spinner /> : null}
          {client ? t('global.save') : t('clients.create')}
        </button>
        <button type="button" className="btn-secondary" onClick={() => onDone(client as Client)}>
          {t('global.cancel')}
        </button>
      </div>
    </form>
  )
}
